import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { FilterService } from '../../services/filter.service';
import { AudienceDashboardAdapterService } from '../../services/audience-engine-adapter.service';
import { AudienceDashboardService } from '../../services/audience-engine.service';

type AudienceType = 'audience' | 'personas';

interface AudienceItem {
  id: string;
  name: string;
  color: string;
  type: AudienceType;
}

interface ConfrontoAudience {
  id: string;
  name: string;
  type: AudienceType;
  trafficoSito: number;
  aderenzaPopolazione: number;
  color: string;
  lightColor: string;
  volume: number;
  effectiveVolume: number;
}

@Component({
  selector: 'app-confronto',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './confronto.html',
  styleUrls: ['./confronto.scss']
})
export class ConfrontoComponent implements OnInit, OnDestroy {
  audiences: ConfrontoAudience[] = [];
  isLoading = true;
  private subscriptions = new Subscription();
  private pendingRequest = false;
  private filtersReady = false;
  tooltipVisible = false;
  tooltipLeft = 0;
  tooltipTop = 0;
  tooltipAudience: ConfrontoAudience | null = null;
  audienceSearchTerm = '';
  displayLimitOptions: number[] = [5, 10, 20, 50];
  selectedDisplayLimit = 10;
  selectedAudienceIds: Set<string> = new Set();
  showAudienceSelector = false;
  private readonly comparisonPalette = ['#1e3a8a', '#3b82f6', '#fbbf24', '#f97316', '#8b5cf6'];

  constructor(
    private filterService: FilterService,
    private audienceDashboardAdapter: AudienceDashboardAdapterService,
    private audienceDashboardService: AudienceDashboardService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.isLoading = true;

    this.subscriptions.add(
      this.filterService.filtersLoaded$.subscribe((loaded) => {
        if (loaded) {
          this.filtersReady = true;
          this.refreshData();
        }
      })
    );

    // Attempt load in case filters were already available
    this.refreshData();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  trackByAudience(_index: number, audience: ConfrontoAudience) {
    return audience.id;
  }

  private refreshData() {
    if (this.pendingRequest) {
      return;
    }

    const publicAudiences = this.filterService.getPublicAudiences() || [];
    const personasAudiences = this.filterService.getPersonasAudiences() || [];

    if (!publicAudiences.length && !personasAudiences.length) {
      if (!this.filtersReady) {
        this.isLoading = true;
      }
      return;
    }

    let combinedAudiences: AudienceItem[] = [
      ...publicAudiences.map((aud: any) => ({
        id: aud.id,
        name: aud.name,
        color: aud.color,
        type: 'audience' as AudienceType
      })),
      ...personasAudiences.map((aud: any) => ({
        id: aud.id,
        name: aud.name,
        color: aud.color,
        type: 'personas' as AudienceType
      }))
    ];

    if (!combinedAudiences.length) {
      const fallbackAudiences = this.filterService.getAvailableAudiences() || [];
      combinedAudiences = fallbackAudiences.map((aud: any) => ({
        id: aud.id,
        name: aud.name,
        color: aud.color,
        type: (aud.type as AudienceType) || 'audience'
      }));
    }

    if (!combinedAudiences.length) {
      this.audiences = [];
      this.isLoading = false;
      return;
    }

    this.loadConfrontoData(combinedAudiences);
  }

  private loadConfrontoData(audienceList: AudienceItem[]) {
    this.isLoading = true;
    this.pendingRequest = true;

    const selectedDevices = this.filterService.getSelectedDevices().map(device => device.id);
    const selectedSources = this.filterService.getSelectedTrafficSources().map(source => source.id);
    const selectedGoal = this.filterService.getSelectedGoal();

    const filterColumns: string[] = [
      ...selectedDevices,
      ...selectedSources,
      ...(selectedGoal ? [selectedGoal.id] : [])
    ];

    const allIds = audienceList.map(aud => aud.id);
    const publicAudiences = audienceList.filter(aud => aud.type === 'audience');
    const personasAudiences = audienceList.filter(aud => aud.type === 'personas');

    const publicIds = publicAudiences.map(aud => aud.id);
    const personaIds = personasAudiences.map(aud => aud.id);

    const publicColors = publicAudiences.reduce((acc: Record<string, string>, aud) => {
      acc[aud.id] = aud.color;
      return acc;
    }, {});

    const personaColors = personasAudiences.reduce((acc: Record<string, string>, aud) => {
      acc[aud.id] = aud.color;
      return acc;
    }, {});

    forkJoin({
      publicTreemap: publicIds.length
        ? this.audienceDashboardAdapter.getAudienceTreemap(publicIds, publicColors, 'audience', filterColumns)
            .pipe(catchError(error => {
              console.error('[CONFRONTO] Error loading public treemap:', error);
              return of([]);
            }))
        : of([]),
      personasTreemap: personaIds.length
        ? this.audienceDashboardAdapter.getAudienceTreemap(personaIds, personaColors, 'personas', filterColumns)
            .pipe(catchError(error => {
              console.error('[CONFRONTO] Error loading personas treemap:', error);
              return of([]);
            }))
        : of([]),
      volumes: this.audienceDashboardAdapter.getPublicAudienceVolumes(allIds)
        .pipe(catchError(error => {
          console.error('[CONFRONTO] Error loading audience volumes:', error);
          return of([]);
        }))
    }).subscribe({
      next: ({ publicTreemap, personasTreemap, volumes }) => {
        const treemapEntries: Array<[string, any]> = [];
        (publicTreemap || []).forEach((item: any) => {
          treemapEntries.push([item.id, item]);
        });
        (personasTreemap || []).forEach((item: any) => {
          treemapEntries.push([item.id, item]);
        });
        const treemapMap = new Map<string, any>(treemapEntries);

        const volumeEntries: Array<[string, any]> = [];
        (volumes || []).forEach((item: any) => {
          const key = item.ColumnName || item.ObjectID || item.id;
          volumeEntries.push([key, item]);
        });
        const volumeMap = new Map<string, any>(volumeEntries);

        const combined = audienceList.map(aud => {
          const treemapData = treemapMap.get(aud.id);
          const volumeData = volumeMap.get(aud.id);

          const trafficoSito = treemapData?.value ?? 0;
          const aderenzaPopolazione = volumeData?.PopulationShare ?? volumeData?.populationShare ?? 0;
          const effectiveVolume = Number(
            treemapData?.user_count ??
            treemapData?.volume ??
            volumeData?.volume ??
            0
          ) || 0;

          return {
            id: aud.id,
            name: aud.name,
            type: aud.type,
            trafficoSito,
            aderenzaPopolazione,
            color: aud.color,
            lightColor: this.lightenColor(aud.color),
            volume: volumeData?.Volumes ?? volumeData?.volume ?? 0,
            effectiveVolume
          };
        });

        const sorted = combined.sort((a, b) => b.trafficoSito - a.trafficoSito);
        this.audiences = sorted.map((aud, index) => {
          const paletteColor = this.comparisonPalette[index % this.comparisonPalette.length];
          return {
            ...aud,
            color: paletteColor,
            lightColor: this.lightenColor(paletteColor)
          };
        });
        this.isLoading = false;
        this.pendingRequest = false;
      },
      error: (error) => {
        console.error('[CONFRONTO] Error loading confronto data:', error);
        this.audiences = [];
        this.isLoading = false;
        this.pendingRequest = false;
      }
    });
  }

  private lightenColor(hex: string, percentage: number = 60): string {
    if (!hex) {
      return '#e5e7eb';
    }

    const normalized = hex.replace('#', '');
    if (normalized.length !== 6) {
      return hex;
    }

    const num = parseInt(normalized, 16);
    const r = (num >> 16) & 0xff;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    const amount = percentage / 100;

    const newR = Math.min(255, Math.round(r + (255 - r) * amount));
    const newG = Math.min(255, Math.round(g + (255 - g) * amount));
    const newB = Math.min(255, Math.round(b + (255 - b) * amount));

    const toHex = (value: number) => value.toString(16).padStart(2, '0');
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  }

  showTooltip(event: MouseEvent, audience: ConfrontoAudience) {
    this.tooltipAudience = audience;
    this.tooltipVisible = true;
    this.updateTooltipPosition(event);
  }

  moveTooltip(event: MouseEvent) {
    if (!this.tooltipVisible) {
      return;
    }
    this.updateTooltipPosition(event);
  }

  hideTooltip() {
    this.tooltipVisible = false;
    this.tooltipAudience = null;
  }

  private updateTooltipPosition(event: MouseEvent) {
    const offset = 12;
    this.tooltipLeft = event.clientX + offset;
    this.tooltipTop = event.clientY + offset;
  }

  formatPercentage(value: number, fractionDigits: number = 0): string {
    return value.toFixed(fractionDigits);
  }

  formatVolume(value: number): string {
    if (!value) {
      return '0';
    }
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  }

  getLeftLabelPosition(percent: number): string {
    const safe = Math.min(Math.max(percent, 0), 100);
    return `calc(min(${safe}%, 100% - 70px) + 12px)`;
  }

  getRightLabelPosition(percent: number): string {
    const safe = Math.min(Math.max(percent, 0), 100);
    return `calc(min(${safe}%, 100% - 70px) + 12px)`;
  }

  get filteredAudiences(): ConfrontoAudience[] {
    let list = this.audiences;

    // Filter by selected audiences if any are selected
    if (this.selectedAudienceIds.size > 0) {
      list = list.filter(aud => this.selectedAudienceIds.has(aud.id));
    }

    if (this.selectedDisplayLimit > 0) {
      return list.slice(0, this.selectedDisplayLimit);
    }

    return list;
  }

  get filteredAudiencesForDropdown(): ConfrontoAudience[] {
    const term = this.audienceSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.audiences;
    }
    return this.audiences.filter(aud => aud.name.toLowerCase().includes(term));
  }

  onLimitChange(value: number) {
    this.selectedDisplayLimit = Number(value) || 0;
  }

  onAudienceSearch(value: string) {
    this.audienceSearchTerm = value;
  }

  toggleAudienceSelector() {
    this.showAudienceSelector = !this.showAudienceSelector;
  }

  toggleAudience(audienceId: string) {
    if (this.selectedAudienceIds.has(audienceId)) {
      this.selectedAudienceIds.delete(audienceId);
    } else {
      this.selectedAudienceIds.add(audienceId);
    }
  }

  isAudienceSelected(audienceId: string): boolean {
    return this.selectedAudienceIds.has(audienceId);
  }

  selectAllAudiences() {
    if (this.selectedAudienceIds.size === this.audiences.length) {
      this.selectedAudienceIds.clear();
    } else {
      this.audiences.forEach(aud => this.selectedAudienceIds.add(aud.id));
    }
  }

  resetAudienceSelection() {
    this.selectedAudienceIds.clear();
  }

  get hasAudienceSelection(): boolean {
    return this.selectedAudienceIds.size > 0;
  }

  get audienceSelectionSummary(): string {
    if (this.selectedAudienceIds.size === 0) {
      return this.translate.instant('confronto.controls.allAudiencesSelected');
    }
    if (this.selectedAudienceIds.size === 1) {
      const selected = this.audiences.find(a => this.selectedAudienceIds.has(a.id));
      return selected?.name || this.translate.instant('confronto.controls.oneSelected');
    }
    return this.translate.instant('confronto.controls.nSelected', { n: this.selectedAudienceIds.size });
  }

  selectAllInsights(): void {
    this.selectedAudienceIds.clear();
    this.audiences
      .filter(aud => aud.type === 'audience')
      .forEach(aud => this.selectedAudienceIds.add(aud.id));
  }

  selectAllPersonas(): void {
    this.selectedAudienceIds.clear();
    this.audiences
      .filter(aud => aud.type === 'personas')
      .forEach(aud => this.selectedAudienceIds.add(aud.id));
  }

  exportToExcel() {
    if (!this.audiences.length) {
      window.alert('Nothing to export');
      return;
    }

    const wsData: (string | number)[][] = [
      ['Audience', 'Type', 'Site Traffic (%)', 'Population Share (%)', 'Volume', 'Effective Volume']
    ];

    this.audiences.forEach(audience => {
      wsData.push([
        audience.name,
        audience.type === 'personas' ? 'Personas' : 'Audience',
        this.formatPercentage(audience.trafficoSito, 1),
        this.formatPercentage(audience.aderenzaPopolazione, 2),
        audience.volume,
        audience.effectiveVolume
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comparison');
    const fileName = `comparison-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  exportToPNG() {
    const exportElement = document.querySelector('.confronto-card') as HTMLElement | null;
    if (!exportElement) {
      window.alert('Nothing to export');
      return;
    }

    html2canvas(exportElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true
    }).then(canvas => {
      canvas.toBlob(blob => {
        if (blob) {
          const fileName = `comparison-${new Date().toISOString().split('T')[0]}.png`;
          saveAs(blob, fileName);
        }
      });
    }).catch(error => {
      console.error('[CONFRONTO] Error exporting PNG:', error);
      window.alert('Error exporting PNG');
    });
  }
}
